import React, { useEffect, useMemo } from 'react'
import { useSetAtom } from 'jotai'
import { useDebounce } from '@node-space/hooks'
import { Approval } from 'constants/General'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import { useWalletQuery } from 'hooks/queries/useWalletQuery'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import RequiresApprovalSuccessModal from 'pages/Approvals/components/RequiresApprovalSuccessModal'
import { IWalletActionApiType } from 'pages/Wallets/hooks/useWalletActionApi'
import { BaseErrorResponse } from 'types/beneficiaries'
import { SourceWalletTypes } from 'types/move/move'
import { PayoutsFeeEstimateResponse } from 'types/payoutsV2'
import { CommonPayoutsForm } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import { WalletAction, WalletTabProps } from '../Modal/WalletModal'
import { selectedWalletAtom } from './atoms/atoms'
import PayAmount from './PayAmount'
import PayComplete from './PayComplete'
import PayConfirm from './PayConfirm'
import PayFormBenficiary from './PayFormBenficiary'
import PayFormBvnkNetworkBeneficiary from './PayFormBvnkNetworkBeneficiary'
import { SourcePayWallet } from './types'
import { getPayFeeRequest } from './utils/payFormUtils'

export type PaySteps = 'form' | 'amount' | 'confirm' | 'complete'

export type PayForm = CommonPayoutsForm & {
  beneficiarySelection?: string

  errorMessage?: string
}

export type PayActionProps = WalletTabProps &
  IWalletActionApiType<PayForm, PaySteps> & {
    payFees?: PayoutsFeeEstimateResponse
    confirmAmount?: () => void
    action?: WalletAction
    feeError?: BaseErrorResponse
  }

/**
 * Pay Tab containing state, form and API data for each step
 */
export const Pay = ({
  loading,
  step,
  form,
  wallets,
  setLoading,
  setStep,
  ...otherProps
}: PayActionProps) => {
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const formValues = form.watch()
  const debouncedAmount = useDebounce(formValues?.amount, 1000)

  const { beneficiaryPaymentType, customerWalletId } = otherProps?.action?.metadata?.pay || {}
  const isNetWorkWallet = beneficiaryPaymentType === PayActionBeneficiaryTypes.NETWORK
  const isMainWallet = beneficiaryPaymentType === PayActionBeneficiaryTypes.FIAT
  const isCustomerWallet = beneficiaryPaymentType === PayActionBeneficiaryTypes.UNDERLYING_CUSTOMER

  const { data: walletId } = useGetWalletLegacyId(customerWalletId)

  const { data: customerWallet, isFetching: isFetchingCustomerWallet } = useWalletQuery(
    Number(walletId),
    !!walletId && isCustomerWallet
  )

  const sourceWallet: SourcePayWallet = useMemo(() => {
    if (isMainWallet || isNetWorkWallet) {
      const mainWallet =
        formValues.walletId &&
        wallets.find(wallet => wallet?.id.toString() === formValues?.walletId)

      if (mainWallet) {
        return {
          walletId: mainWallet?.lsid,
          legacyWalletId: mainWallet?.id,
          walletName: mainWallet?.description,
          currency: mainWallet?.currency?.code,
          balance: mainWallet?.available,
          walletType: SourceWalletTypes.MAIN,
          supportedTransferDestinations: mainWallet?.supportedTransferDestinations,
          supportsInternalBvnkNetworkTransfers: mainWallet?.supportsInternalBvnkNetworkTransfers,
          isLegacyWallet: !mainWallet?.supportedTransferDestinations?.length,
        } as SourcePayWallet
      }
    } else if (isCustomerWallet) {
      if (customerWallet) {
        return {
          walletId: customerWallet?.lsid,
          legacyWalletId: walletId,
          walletName: customerWallet?.description,
          currency: customerWallet?.currency?.code,
          balance: customerWallet?.available,
          walletType: SourceWalletTypes.CUSTOMER,
          supportedTransferDestinations: customerWallet?.supportedTransferDestinations,
          supportsInternalBvnkNetworkTransfers:
            customerWallet?.supportsInternalBvnkNetworkTransfers,
          isLegacyWallet: !customerWallet?.supportedTransferDestinations?.length,
        } as SourcePayWallet
      }
    }
  }, [
    otherProps?.action,
    formValues.walletId,
    wallets,
    isMainWallet,
    isCustomerWallet,
    customerWallet,
    walletId,
  ])

  useEffect(() => {
    setSourceWalletAtom(sourceWallet)
  }, [sourceWallet])

  const amountSummary = {
    amount: debouncedAmount,
    currency: sourceWallet?.currency,
  }
  const { approvalsIsRequiredForFlow, isFetchingApprovalPreference } = useApprovalsConfigForFlow(
    Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT,
    amountSummary
  )

  const isPayoutsV2Enabled = !!sourceWallet?.supportedTransferDestinations?.length

  const feeRequest = useMemo(() => {
    return getPayFeeRequest(debouncedAmount, formValues, sourceWallet, isPayoutsV2Enabled)
  }, [
    debouncedAmount,
    formValues?.beneficiarySelection,
    formValues?.reference,
    sourceWallet,
    isPayoutsV2Enabled,
  ])

  const payoutFeeEstimateParams: PayoutsFeeEstimateParams = {
    request: feeRequest,
    isEnabled: false,
  }

  const {
    data: payFees,
    refetch: refetchPayFees,
    isFetching: isFetchingPayFees,
    error: payFeeError,
  } = usePayoutsFeeEstimateQuery(payoutFeeEstimateParams)

  const confirmAmount = async () => {
    setLoading(false)
    setStep('confirm')
  }

  useEffect(() => {
    if (debouncedAmount && !!sourceWallet && !sourceWallet?.supportsInternalBvnkNetworkTransfers) {
      setLoading(true)
      refetchPayFees()

      setLoading(false)
    }
  }, [debouncedAmount, sourceWallet?.walletId])

  useEffect(() => {
    if (step === 'form') {
      form.setValue('amount', '')
    }
  }, [step])

  const isBusy = useMemo(
    () => !wallets?.length || loading || isFetchingPayFees || isFetchingCustomerWallet,
    [wallets, loading, isFetchingPayFees, isFetchingCustomerWallet]
  )

  const isBvnkNetworkPayment = sourceWallet?.supportsInternalBvnkNetworkTransfers || isNetWorkWallet

  const isRequiresApprovalModalVisible =
    approvalsIsRequiredForFlow && !isFetchingApprovalPreference && !isBvnkNetworkPayment

  const handleOnPayClose = () => {
    props?.dismissAction?.()
  }

  const props = {
    payFees,
    loading: loading || isFetchingPayFees,
    step,
    form,
    setLoading,
    wallets,
    setStep,
    confirmAmount,
    handleOnPayClose,
    ...otherProps,
  }

  const renderStep = () => {
    switch (step) {
      case 'form':
        return isBvnkNetworkPayment ? (
          <PayFormBvnkNetworkBeneficiary {...props} loading={isBusy} />
        ) : (
          <PayFormBenficiary {...props} loading={isBusy} />
        )
      case 'amount':
        return <PayAmount {...props} feeError={payFeeError as BaseErrorResponse} />
      case 'confirm':
        return <PayConfirm {...props} />
      case 'complete':
        return isRequiresApprovalModalVisible ? (
          <RequiresApprovalSuccessModal onClose={props?.dismissAction} actionType={props?.action} />
        ) : (
          !isFetchingApprovalPreference && <PayComplete {...props} />
        )
    }
  }

  return renderStep()
}
